import React from 'react';
import ToolSlotsEditor from './ToolSlotsEditor';

const inputStyle = {
  width:'100%', boxSizing:'border-box', background:'var(--ni-panel)',
  color:'var(--ni-text)', border:'1px solid var(--ni-border-soft)',
  borderRadius:6, padding:'6px 8px', fontSize:13,
};
const hintStyle = { fontSize:12, color:'var(--ni-text-muted)', lineHeight:1.5 };
const buttonStyle = {
  background:'var(--ni-panel)', color:'var(--ni-text)',
  border:'1px solid var(--ni-border-soft)', borderRadius:6, padding:'6px 9px', cursor:'pointer',
};

export function FlowExecutionPolicy({ value, onChange, disabled, sites = [], onSitesChange }) {
  return <section aria-label="Production execution policy">
    <h3 style={{ fontSize:14 }}>Production execution</h3>
    <label style={{ display:'flex', gap:8, alignItems:'center', fontSize:13 }}>
      <input type="checkbox" checked={!!value?.enabled} disabled={disabled}
        onChange={e => onChange({ enabled:e.target.checked })} />
      Require controlled station execution
    </label>
    {onSitesChange && <><label style={{ display: 'block', fontSize: 13, marginTop: 10 }}>Execution sites (comma separated)
      <input style={inputStyle} disabled={disabled} value={sites.join(', ')}
        onChange={event => onSitesChange(event.target.value === '' ? [] : event.target.value.split(',').map(site => site.trim()))} />
    </label><p style={hintStyle}>Use exact site identifiers. A station with tool slots requires a site before its approved software can be resolved.</p></>}
    <p style={hintStyle}>Requires released, preserved document revisions before a unit can start.
      Timers, required results, evidence and sign-offs use the saved station settings.
      Save Canvas to retain changes; this setting does not release the process.</p>
  </section>;
}

export default function StationExecutionSettings({ value, onChange, disabled }) {
  const execution = value || {};
  const checkpoints = execution.checkpoints || [];
  const update = patch => onChange({ ...execution, ...patch });
  const updateCheckpoint = (index, patch) => update({ checkpoints:checkpoints.map((c,i) => i === index ? { ...c, ...patch } : c) });
  const addCheckpoint = () => {
    let next = 1;
    const existingKeys = new Set(checkpoints.map(c => c.key));
    while (existingKeys.has(`checkpoint-${next}`)) next += 1;
    update({ checkpoints:[...checkpoints, { key:`checkpoint-${next}`, label:'', resultType:'pass_fail', required:true, evidenceRequired:false }] });
  };
  return <section aria-label="Station execution settings" style={{ borderTop:'1px solid var(--ni-border-soft)', paddingTop:12, marginBottom:18 }}>
    <h3 style={{ fontSize:13, margin:'0 0 8px' }}>Execution requirements</h3>
    <p style={hintStyle}>Enforced when controlled station execution is enabled for the flow.
      A failed or held result prevents completion. The operator starts a required timer when the
      physical timed operation begins; setup time is excluded.</p>
    <label style={{ display:'block', fontSize:12, marginBottom:10 }}>
      Minimum timed duration (minutes)
      <input type="number" min="0" step="any" value={(execution.minimumDurationSeconds || 0) / 60}
        disabled={disabled} onChange={e => update({ minimumDurationSeconds:Math.round(Number(e.target.value) * 60) })}
        style={inputStyle} />
    </label>
    <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, marginBottom:12 }}>
      <input type="checkbox" checked={!!execution.independentReviewRequired} disabled={disabled}
        onChange={e => update({ independentReviewRequired:e.target.checked })} />
      Require a different reviewer to sign off
    </label>
    {checkpoints.map((checkpoint,index) => <fieldset key={index} style={{ margin:'0 0 10px', border:'1px solid var(--ni-border-soft)', borderRadius:6, padding:8 }}>
      <legend style={{ fontSize:12 }}>Result {index + 1}</legend>
      <label style={{ display:'block', fontSize:12, marginBottom:8 }}>Result key
        <input value={checkpoint.key || ''} disabled={disabled} style={inputStyle}
          onChange={e => updateCheckpoint(index,{ key:e.target.value })} />
      </label>
      <label style={{ display:'block', fontSize:12, marginBottom:8 }}>Result label
        <input value={checkpoint.label || ''} disabled={disabled} style={inputStyle}
          onChange={e => updateCheckpoint(index,{ label:e.target.value })} />
      </label>
      <label style={{ display:'block', fontSize:12, marginBottom:8 }}>Result type
        <select value={checkpoint.resultType || 'pass_fail'} disabled={disabled} style={inputStyle}
          onChange={e => updateCheckpoint(index,{ resultType:e.target.value, minValue:null, maxValue:null, unit:'' })}>
          <option value="pass_fail">Pass / fail</option><option value="number">Measurement</option><option value="text">Text record</option>
        </select>
      </label>
      {checkpoint.resultType === 'number' && <>
        <label style={{ display:'block', fontSize:12, marginBottom:8 }}>Unit
          <input value={checkpoint.unit || ''} disabled={disabled} style={inputStyle}
            onChange={e => updateCheckpoint(index,{ unit:e.target.value })} />
        </label>
        <div style={{ display:'flex', gap:8, marginBottom:8 }}>
          {['minValue','maxValue'].map((key,i) => <label key={key} style={{ flex:1, fontSize:12 }}>{i === 0 ? 'Minimum' : 'Maximum'}
            <input type="number" step="any" value={checkpoint[key] ?? ''} disabled={disabled} style={inputStyle}
              onChange={e => updateCheckpoint(index,{ [key]:e.target.value === '' ? null : Number(e.target.value) })} />
          </label>)}
        </div>
      </>}
      <label style={{ display:'flex', gap:8, fontSize:12, marginBottom:8 }}>
        <input type="checkbox" checked={checkpoint.required !== false} disabled={disabled}
          onChange={e => updateCheckpoint(index,{ required:e.target.checked })} />Required result
      </label>
      <label style={{ display:'flex', gap:8, fontSize:12, marginBottom:8 }}>
        <input type="checkbox" checked={!!checkpoint.evidenceRequired} disabled={disabled}
          onChange={e => updateCheckpoint(index,{ evidenceRequired:e.target.checked })} />Require uploaded evidence
      </label>
      {!disabled && <button type="button" style={buttonStyle}
        onClick={() => update({ checkpoints:checkpoints.filter((_,i) => i !== index) })}>Remove result {index + 1}</button>}
    </fieldset>)}
    {!checkpoints.length && <p style={hintStyle}>No structured results configured for this station.</p>}
    {!disabled && <button type="button" onClick={addCheckpoint} style={buttonStyle}>Add required result</button>}
    <ToolSlotsEditor value={execution.toolSlots || []} disabled={disabled} onChange={toolSlots => update({ toolSlots })} />
  </section>;
}
